import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/site";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, LogOut, Search, Trash2, RefreshCw, Mail, Phone } from "lucide-react";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminLeads() {
  const { user, loading, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await authAxios().get("/leads");
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); navigate("/admin"); }
      else toast.error("Failed to load leads");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.phone, l.company, l.service, l.message].some((v) =>
        (v || "").toLowerCase().includes(t)
      )
    );
  }, [q, leads]);

  const exportCsv = () => {
    if (!filtered.length) { toast.info("No leads to export"); return; }
    const header = ["Name", "Email", "Phone", "Company", "Service", "Message", "Received", "Email sent"];
    const rows = filtered.map((l) => [l.name, l.email, l.phone, l.company, l.service, l.message, l.created_at, l.email_sent ? "yes" : "no"]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `digidoot-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const del = async (id) => {
    try {
      await authAxios().delete(`/leads/${id}`);
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success("Lead deleted");
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return null;
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <main data-testid="admin-leads-page" className="min-h-screen bg-brand-beige">
      {/* Top bar */}
      <header className="border-b border-brand-border bg-brand-beige sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DigiDoot" className="w-9 h-9 rounded-full" />
            <div>
              <p className="font-display text-xl leading-none">DigiDoot</p>
              <p className="text-[10px] uppercase tracking-[0.24em] text-brand-charcoal/50 mt-1">Admin · Leads</p>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-brand-charcoal/70">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); navigate("/admin"); toast.success("Signed out"); }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-border text-sm hover:bg-brand-gold hover:text-white transition"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <p className="eyebrow mb-4">Inbox</p>
            <h1 className="font-display text-4xl md:text-5xl leading-tight font-medium">
              {filtered.length} <span className="italic text-brand-brown">{filtered.length === 1 ? "lead" : "leads"}</span>.
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/50" />
              <Input
                data-testid="admin-leads-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email, service…"
                className="pl-9 w-72 bg-white"
              />
            </div>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-border text-sm bg-brand-beige hover:bg-white transition"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold text-brand-charcoal font-semibold text-sm hover:bg-brand-gold hover:text-white transition"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="bg-brand-beige border border-brand-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-white/60">
                <TableHead className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/70">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/70">Contact</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/70">Company</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/70">Service</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/70">Message</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/70">Received</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.2em] text-brand-charcoal/70 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {busy && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-brand-charcoal/60">Loading…</TableCell></TableRow>
              )}
              {!busy && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-14 text-brand-charcoal/60">
                  No leads yet. Share your site to start collecting.
                </TableCell></TableRow>
              )}
              {!busy && filtered.map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="hover:bg-brand-beige/60">
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-2"><Mail size={12} className="text-brand-brown" /><a href={`mailto:${l.email}`} className="hover:text-brand-brown">{l.email}</a></div>
                    <div className="flex items-center gap-2 mt-1"><Phone size={12} className="text-brand-brown" /><a href={`tel:${l.phone}`} className="hover:text-brand-brown">{l.phone}</a></div>
                  </TableCell>
                  <TableCell className="text-sm text-brand-charcoal/70">{l.company || "—"}</TableCell>
                  <TableCell className="text-sm"><span className="inline-block px-2 py-1 rounded-full bg-white text-xs">{l.service}</span></TableCell>
                  <TableCell className="text-sm text-brand-charcoal/80 max-w-sm">
                    <p className="line-clamp-3">{l.message}</p>
                  </TableCell>
                  <TableCell className="text-xs text-brand-charcoal/60 whitespace-nowrap">
                    {fmtDate(l.created_at)}
                    {l.email_sent && <span className="ml-2 text-brand-brown">· emailed</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`lead-delete-${l.id}`} className="p-2 rounded-full hover:bg-white" aria-label="Delete">
                          <Trash2 size={14} className="text-brand-charcoal/60" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently removes {l.name}&apos;s entry from your database.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(l.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </main>
  );
}
